/**
 * @fileoverview Esperar a confirmação do e-mail sem sair da tela (03/11/2026, a
 * lógica saiu do assistente da IA para a contratação usar a mesma): relê o
 * servidor ao abrir (pode já ter confirmado, sem a sessão saber), quando outra
 * aba avisa pelo armazenamento do navegador e ao voltar para esta. Sem perceber
 * (confirmou no celular, com a aba em primeiro plano), o "Já confirmei" confere.
 * Confirmado, relê também a sessão: a faixa do topo some.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { EMAIL_CONFIRMED_KEY, errorText } from "../components/AuthLayout/authHelpers";
import api, { openApi } from "../services/api";
import { i18n } from "../translate/i18n";

const t = (key, options) => i18n.t(`aiSetup.confirmEmail.${key}`, options);

export const useEmailConfirmation = ({ email, onConfirmed, active = true }) => {
  const [checking, setChecking] = useState(false);
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const isConfirmed = useCallback(async () => {
    const { data } = await api.get("/onboarding/setup");
    if (!data?.emailVerified || !mountedRef.current) return false;
    // O aviso que o useAuth ouve para reler a sessão.
    window.dispatchEvent(new Event("auth:error"));
    onConfirmed();
    return true;
  }, [onConfirmed]);

  // A conferência sozinha, sem aviso: se falhar, o botão continua ali.
  useEffect(() => {
    if (!active) return undefined;
    const recheck = () => {
      isConfirmed().catch(() => undefined);
    };
    const confirmedElsewhere = (event) => {
      if (event.key === EMAIL_CONFIRMED_KEY) recheck();
    };
    const becameVisible = () => {
      if (document.visibilityState === "visible") recheck();
    };
    recheck();
    window.addEventListener("focus", recheck);
    window.addEventListener("storage", confirmedElsewhere);
    document.addEventListener("visibilitychange", becameVisible);
    return () => {
      window.removeEventListener("focus", recheck);
      window.removeEventListener("storage", confirmedElsewhere);
      document.removeEventListener("visibilitychange", becameVisible);
    };
  }, [active, isConfirmed]);

  const check = async () => {
    setChecking(true);
    setNotice("");
    setError("");
    try {
      // Confirmado, quem chamou tira o pedido da tela.
      if (await isConfirmed()) return;
      if (mountedRef.current) setNotice(t("notYet"));
    } catch (err) {
      if (mountedRef.current) setError(errorText(err));
    }
    if (mountedRef.current) setChecking(false);
  };

  // Um clique duplo gastaria duas das tentativas e o segundo link invalidaria o primeiro.
  const resend = async () => {
    if (sending) return;
    setSending(true);
    setNotice("");
    setError("");
    try {
      await openApi.post("/auth/verify-email/resend", { email });
      if (mountedRef.current) setNotice(t("resent"));
    } catch (err) {
      if (mountedRef.current) setError(errorText(err));
    }
    if (mountedRef.current) setSending(false);
  };

  return { check, resend, checking, sending, notice, error };
};
